import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useData } from "../context/DataContext";
import { useAuth } from "../context/AuthContext";
import { useHierarchy } from "../hooks/useHierarchy";
import { useAbility } from "@casl/react";
import { AbilityContext } from "../context/casl/AbilityContext";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "./ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Search, Filter, Eye, User, Building2, Calendar, FileText, Users, Clock, Download, Archive, ChevronDown, FileSpreadsheet, FileJson, Plus, Settings, Edit, Trash2 } from "lucide-react";
import { QualificationStatusBadge } from "./qualifications/QualificationStatusBadge";
import { AddEditQualificationDialog } from "./qualifications/AddEditQualificationDialog";
import { calculateQualificationStatus, formatDate, getDaysUntilExpiry } from "../utils/qualification-utils";
import { Qualification, User as UserType } from "../types";
import { DeleteQualificationDialog } from "./qualifications/DeleteQualificationDialog";
import { Badge } from "./ui/badge";
import { Can } from "./Can";
import { ACTIONS, RESOURCES } from "../constants/permissions.constants";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from "./ui/dialog";
import { QualificationDrawer } from "./qualifications/QualificationDrawer";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "./ui/dropdown-menu";
import { canViewProfile } from "../utils/user-logic";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Checkbox } from "./ui/checkbox";
import { Separator } from "./ui/separator";

export const TrainingQualificationRegister: React.FC = () => {
  const { 
    qualifications = [], 
    qualificationTypes = [], 
    qualificationGroups = [], 
    departments = [], 
    users = [], 
    deleteQualification, 
    fetchQualifications 
  } = useData() || {};
  const { currentUser } = useAuth() || {};
  const { canManageUser } = useHierarchy();
  const ability = useAbility(AbilityContext);
  const navigate = useNavigate();

  const [searchTerm, setSearchTerm] = useState("");
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [selectedPeople, setSelectedPeople] = useState<string[]>([]);
  const [departmentFilter, setDepartmentFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [timeframeFilter, setTimeframeFilter] = useState("all");

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedQualification, setSelectedQualification] = useState<Qualification | null>(null);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [qualificationToDelete, setQualificationToDelete] = useState<string | null>(null);
  const [filterSearch, setFilterSearch] = useState("");


  useEffect(() => {
    fetchQualifications();
  }, []);

  // Filter logic
  const filteredQualifications = useMemo(() => {
    return (qualifications || []).filter((q: Qualification) => {
      const status = calculateQualificationStatus(q);

      // Search term (name or person name)
      const matchesSearch =
        q.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        q.person?.name.toLowerCase().includes(searchTerm.toLowerCase());

      // Multi-select Worker/Department filter
      const personId = q.person?.id?.toString();
      const personDeptId = (typeof q.person?.department === 'object' ? q.person?.department.id : q.person?.department)?.toString();

      const hasPeopleFilter = selectedPeople.length > 0;
      const hasGroupFilter = selectedGroupIds.length > 0;

      let matchesWorkerParams = true;
      if (hasPeopleFilter || hasGroupFilter) {
        const isPersonSelected = hasPeopleFilter && selectedPeople.includes(personId);
        let isGroupMember = false;
        if (hasGroupFilter && personId) {
          isGroupMember = selectedGroupIds.some(groupId => {
            const group = (qualificationGroups || []).find(g => g.id.toString() === groupId);
            return group?.users?.some(u => u.id.toString() === personId);
          });
        }
        matchesWorkerParams = isPersonSelected || isGroupMember;
      }

      // Standalone Department filter
      const matchesStandaloneDept = departmentFilter === "all" || personDeptId == departmentFilter;

      // Status filter
      const matchesStatus = statusFilter === "all" || status === statusFilter;

      // Timeframe filter (on issue date)
      let matchesTimeframe = true;
      if (timeframeFilter !== "all" && q.issueDate) {
        const issueDate = new Date(q.issueDate);
        const now = new Date();
        const diffDays = (now.getTime() - issueDate.getTime()) / (1000 * 3600 * 24);

        if (timeframeFilter === "last30") matchesTimeframe = diffDays <= 30;
        else if (timeframeFilter === "last90") matchesTimeframe = diffDays <= 90;
        else if (timeframeFilter === "thisYear") matchesTimeframe = issueDate.getFullYear() === now.getFullYear();
      }

      return matchesSearch && matchesWorkerParams && matchesStandaloneDept && matchesStatus && matchesTimeframe;
    });
  }, [qualifications, searchTerm, selectedPeople, selectedGroupIds, departmentFilter, statusFilter, timeframeFilter]);

  const handleEdit = (q: Qualification, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/people/${q.person?.id}/qualifications/edit/${q.id}`);
  };

  const handleRowClick = (q: Qualification) => {
    setSelectedQualification(q);
    setIsDrawerOpen(true);
  };


  const handleExport = async (format: 'csv' | 'excel') => {
    try {
      const params = new URLSearchParams();
      if (selectedGroupIds.length > 0) params.append("groupIds", selectedGroupIds.join(','));
      if (selectedPeople.length > 0) params.append("personIds", selectedPeople.join(','));
      if (departmentFilter !== "all") params.append("departmentId", departmentFilter);
      if (statusFilter !== "all") params.append("status", statusFilter);
      if (timeframeFilter !== "all") params.append("timeframe", timeframeFilter);
      if (searchTerm) params.append("searchTerm", searchTerm);
      params.append("format", format);

      const token = localStorage.getItem("token");
      const baseUrl = process.env.REACT_APP_API_BASE_URL || "http://localhost:5001";
      const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;

      const response = await fetch(`${cleanBaseUrl}/api/qualifications/export?${params.toString()}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error("Failed to export data");

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `qualifications_export.${format === 'excel' ? 'xlsx' : 'csv'}`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Failed to generate export file");
    }
  };

  const confirmDelete = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setQualificationToDelete(id);
    setIsDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (qualificationToDelete) {
      try {
        await deleteQualification(qualificationToDelete);
        setIsDeleteDialogOpen(false);
        setQualificationToDelete(null);
      } catch (error) {
        console.error("Delete failed:", error);
      }
    }
  };

  // Calculate counts for Groups
  const getGroupUserCount = (groupId: string) => {
    const group = qualificationGroups.find(g => g.id.toString() === groupId);
    if (!group || !group.users) return 0;

    return group.users.filter((userInGroup: any) => {
      const fullUser = users.find(u => u.id === userInGroup.id);
      return fullUser ? (fullUser.isActive && !fullUser.deleted) : true;
    }).length;
  };

  const togglePeopleSelection = (id: string) => {
    setSelectedPeople(prev =>
      prev.includes(id) ? prev.filter(p => p !== id) : [...prev, id]
    );
  };

  const toggleGroupSelection = (id: string) => {
    setSelectedGroupIds(prev =>
      prev.includes(id) ? prev.filter(g => g !== id) : [...prev, id]
    );
  };

  const clearAllFilters = () => {
    setSelectedPeople([]);
    setSelectedGroupIds([]);
    setDepartmentFilter("all");
    setSearchTerm("");
    setStatusFilter("all");
    setTimeframeFilter("all");
  };

  const hasAnyFilter = selectedPeople.length > 0 || selectedGroupIds.length > 0 || departmentFilter !== "all" || statusFilter !== "all" || timeframeFilter !== "all" || searchTerm !== "";

  return (
    <div className="space-y-4">
      {/* Top Header/Action Bar */}
      <div className="flex flex-col sm:flex-row justify-end items-start sm:items-center gap-4">
        <div className="flex flex-wrap items-center gap-2 w-full sm:w-auto">
          <Can I={ACTIONS.READ} a={RESOURCES.QUALIFICATION}>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button className="gap-2 bg-slate-900 hover:bg-slate-800 text-white sm:flex-none">
                  <Download size={16} />
                  <span>Export</span>
                  <ChevronDown size={14} className="opacity-50" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuItem onClick={() => handleExport('csv')} className="gap-2 cursor-pointer">
                  <FileSpreadsheet size={16} className="text-slate-500" />
                  <span>Export as CSV</span>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => handleExport('excel')} className="gap-2 cursor-pointer">
                  <FileJson size={16} className="text-slate-500" />
                  <span>Export as Excel</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </Can>
        </div>
      </div>

      {/* Filters Area (Stacked 2-row layout) */}
      <div className="bg-slate-100/50 rounded-xl p-4 space-y-4">
        {/* Row 1: Search */}
        <div className="relative w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by staff name or qualification..."
            className="pl-9 bg-white w-full border-slate-200 focus-visible:ring-slate-200"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Row 2: Selectors */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-4">
          {/* Redesigned Worker Popover */}
          <div className="min-w-0">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="bg-white border-slate-200 w-full justify-between font-normal text-slate-700 h-10 px-3">
                  <div className="flex items-center gap-2 truncate">
                    <Users size={14} className="text-slate-400 shrink-0" />
                    <span className="truncate">
                      {(selectedPeople.length + selectedGroupIds.length) > 0
                        ? `Workers (${selectedPeople.length + selectedGroupIds.length})`
                        : "Workers"}
                    </span>
                  </div>
                  <ChevronDown size={14} className="text-slate-400 shrink-0" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-80 p-0" align="start">
                <div className="flex flex-col max-h-[480px]">
                  <div className="p-3 border-b border-slate-100">
                    <div className="relative">
                      <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                      <Input
                        placeholder="Search groups or staff..."
                        className="pl-8 h-9 text-sm bg-slate-50 border-none focus-visible:ring-slate-200"
                        value={filterSearch}
                        onChange={(e) => setFilterSearch(e.target.value)}
                      />
                    </div>
                  </div>

                  <div className="flex-1 overflow-y-auto p-3 space-y-4 custom-scrollbar">
                    {/* Groups Section */}
                    <div className="space-y-2">
                      <h3 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider px-1">Groups</h3>
                      <div className="space-y-1">
                        {(() => {
                          const filteredGroups = qualificationGroups.filter(g => !g.isArchived && g.label.toLowerCase().includes(filterSearch.toLowerCase()));
                          if (filteredGroups.length === 0) {
                            return <p className="text-xs text-slate-400 py-3 px-1 italic">No groups found</p>;
                          }
                          return filteredGroups.map((group) => {
                            const isSelected = selectedGroupIds.includes(group.id.toString());
                            return (
                              <div
                                key={group.id}
                                onClick={() => toggleGroupSelection(group.id.toString())}
                                className={`flex items-center gap-3 py-1.5 px-3 rounded-md cursor-pointer transition-all ${isSelected
                                    ? "bg-slate-900 text-white shadow-sm"
                                    : "text-slate-700 hover:bg-slate-50"
                                  }`}
                              >
                                <Checkbox
                                  id={`group-${group.id}`}
                                  checked={isSelected}
                                  className={`h-4 w-4 rounded border-slate-300 pointer-events-none ${isSelected ? "border-white bg-white data-[state=checked]:bg-white data-[state=checked]:text-slate-900" : "data-[state=checked]:bg-slate-900"}`}
                                />
                                <label
                                  htmlFor={`group-${group.id}`}
                                  className="text-sm font-medium flex-1 cursor-pointer truncate pointer-events-none"
                                >
                                  {group.label}
                                </label>
                                <span className={`text-[10px] ${isSelected ? "text-slate-400" : "text-slate-400"}`}>
                                  {getGroupUserCount(group.id.toString())}
                                </span>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>

                    <div className="pt-2">
                      <Separator className="bg-slate-100" />
                    </div>

                    {/* Individual Staff Section */}
                    <div className="space-y-2">
                      <h3 className="text-[11px] font-bold text-slate-400 uppercase tracking-wider px-1">Individual Staff</h3>
                      <div className="space-y-0.5">
                        {(() => {
                          const filteredUsers = users.filter(u => u.isActive && !u.deleted && !u.archived && u.name.toLowerCase().includes(filterSearch.toLowerCase()));
                          if (filteredUsers.length === 0) {
                            return <p className="text-xs text-slate-400 py-3 px-1 italic">No staff found</p>;
                          }
                          return filteredUsers.map((user) => (
                            <div
                              key={user.id}
                              className="flex items-center gap-3 py-1.5 px-2 hover:bg-slate-50 rounded-md transition-colors cursor-pointer group"
                              onClick={() => togglePeopleSelection(user.id.toString())}
                            >
                              <Checkbox
                                id={`user-${user.id}`}
                                checked={selectedPeople.includes(user.id.toString())}
                                className="h-4 w-4 rounded border-slate-300 pointer-events-none data-[state=checked]:bg-slate-900 data-[state=checked]:border-slate-900"
                              />
                              <label
                                htmlFor={`user-${user.id}`}
                                className="text-sm font-medium text-slate-700 cursor-pointer flex-1 group-hover:text-slate-900 pointer-events-none"
                              >
                                {user.name}
                              </label>
                            </div>
                          ));
                        })()}
                      </div>
                    </div>
                  </div>

                  {(selectedPeople.length + selectedGroupIds.length) > 0 && (
                    <div className="p-2 bg-slate-50 border-t border-slate-100 flex justify-end">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSelectedPeople([]);
                          setSelectedGroupIds([]);
                        }}
                        className="text-xs text-slate-500 hover:text-slate-900 h-8"
                      >
                        Clear All
                      </Button>
                    </div>
                  )}
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {/* Restore Department Filter */}
          <div className="min-w-0">
            <Select value={departmentFilter} onValueChange={setDepartmentFilter}>
              <SelectTrigger className="bg-white border-slate-200 w-full h-10 px-3">
                <div className="flex items-center gap-2 truncate w-full">
                  <Building2 size={14} className="text-slate-400 shrink-0" />
                  <SelectValue placeholder="All Departments" className="truncate" />
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Departments</SelectItem>
                {(departments || []).map(dept => (
                  <SelectItem key={dept.id} value={dept.id.toString()}>{dept.departmentName}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Status Filter */}
          <div className="min-w-0">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="bg-white border-slate-200 w-full h-10 px-3">
                <div className="flex items-center gap-2 truncate w-full">
                  <Filter size={14} className="text-slate-400 shrink-0" />
                  <SelectValue placeholder="All Status" className="truncate" />
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="Active">Active</SelectItem>
                <SelectItem value="Expiring Soon">Expiring Soon</SelectItem>
                <SelectItem value="Expired">Expired</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Timeframe Filter */}
          <div className="min-w-0">
            <Select value={timeframeFilter} onValueChange={setTimeframeFilter}>
              <SelectTrigger className="bg-white border-slate-200 w-full h-10 px-3">
                <div className="flex items-center gap-2 truncate w-full">
                  <Clock size={14} className="text-slate-400 shrink-0" />
                  <SelectValue placeholder="All Timeframe" className="truncate" />
                </div>
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Timeframe</SelectItem>
                <SelectItem value="last30">Last 30 Days</SelectItem>
                <SelectItem value="last90">Last 90 Days</SelectItem>
                <SelectItem value="thisYear">This Year</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Reset Button Slot */}
          <div className="flex items-center justify-start md:justify-center">
            {hasAnyFilter && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearAllFilters}
                className="text-slate-500 hover:text-slate-900 whitespace-nowrap h-10"
              >
                Reset Filters
              </Button>
            )}
          </div>
        </div>
      </div>

      {/* Register Table */}
      <Card className="border-slate-100 shadow-sm">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-transparent hover:bg-transparent border-slate-100">
                  <TableHead className="font-normal py-3 text-slate-900 underline underline-offset-4 decoration-slate-200">Full Name</TableHead>
                  <TableHead className="font-normal py-3 text-slate-900">Qualification Name</TableHead>
                  <TableHead className="font-normal py-3 text-slate-900">Issuing Authority</TableHead>
                  <TableHead className="font-normal py-3 text-slate-900">Issue Date</TableHead>
                  <TableHead className="font-normal py-3 text-slate-900">Expiry Date</TableHead>
                  <TableHead className="font-normal py-3 text-slate-900">Status</TableHead>
                  <TableHead className="font-normal py-3 text-slate-900">Attachments</TableHead>
                  <TableHead className="text-right font-normal py-3 text-slate-900">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredQualifications.length > 0 ? (
                  filteredQualifications.map((q: Qualification) => {
                    const status = calculateQualificationStatus(q);
                    const personId = q.person?.id;
                    const canViewProfileLink = canViewProfile(personId, currentUser, ability, canManageUser);
                    const canViewDetails = canViewProfileLink || ability.can(ACTIONS.READ, RESOURCES.QUALIFICATION);

                    return (
                      <TableRow
                        key={q.id}
                        className={`${canViewDetails ? "cursor-pointer hover:bg-slate-50/80" : "cursor-default opacity-80"} transition-colors border-slate-100`}
                        onClick={() => {
                          if (canViewDetails) {
                            handleRowClick(q);
                          } else {
                            toast.error("Access Denied: You do not have permission to view details for this record.");
                          }
                        }}
                      >
                        <TableCell className="font-normal text-slate-900">
                          {canViewProfileLink ? (
                            <a
                              href={`/people/${personId}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-indigo-600 hover:text-indigo-900 hover:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              {q.person?.name}
                            </a>

                          ) : (
                            <span className="text-slate-500">{q.person?.name}</span>
                          )}
                        </TableCell>
                        <TableCell className="text-slate-700">
                          {q.name}
                        </TableCell>
                        <TableCell className="text-slate-600">
                          {q.issuingAuthority || "N/A"}
                        </TableCell>
                        <TableCell className="text-slate-600">
                          {q.issueDate ? formatDate(q.issueDate) : "N/A"}
                        </TableCell>
                        <TableCell className="text-slate-600">
                          {q.expiryDate ? formatDate(q.expiryDate) : "N/A"}
                        </TableCell>
                        <TableCell>
                          <QualificationStatusBadge status={calculateQualificationStatus(q)} />
                        </TableCell>
                        <TableCell className="text-slate-400">
                          {q.attachments && q.attachments.length > 0 ? q.attachments.length : "---"}
                        </TableCell>
                        <TableCell className="text-right">
                          {canViewDetails && (
                            <div className="flex justify-end gap-1">
                              <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-slate-900" onClick={(e) => { e.stopPropagation(); handleRowClick(q); }}>
                                <Eye size={16} />
                              </Button>
                              <Can I={ACTIONS.UPDATE} a={RESOURCES.QUALIFICATION}>
                                <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-indigo-600" onClick={(e) => { e.stopPropagation(); handleEdit(q, e); }}>
                                  <Edit size={16} />
                                </Button>
                              </Can>
                              <Can I={ACTIONS.DELETE} a={RESOURCES.QUALIFICATION}>
                                <Button variant="ghost" size="icon" className="h-8 w-8 text-slate-400 hover:text-red-600" onClick={(e) => { e.stopPropagation(); confirmDelete(q.id, e); }}>
                                  <Trash2 size={16} />
                                </Button>
                              </Can>
                            </div>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} className="h-32 text-center text-muted-foreground">
                      No qualifications found matching your filters.
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <QualificationDrawer
        open={isDrawerOpen}
        onOpenChange={setIsDrawerOpen}
        qualification={selectedQualification}
        viewOnly={selectedQualification ? !canManageUser(selectedQualification.person?.id) && !ability.can(ACTIONS.UPDATE, RESOURCES.QUALIFICATION) : false}
      />

      <DeleteQualificationDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onConfirm={handleDelete}
      />
    </div>
  );
};